import {
    type AdminAgentPageContextResolution,
    type AdminAgentPageContextResolver,
    normalizeAdminAgentPageContextEnvironment,
    requireAdminAgentPageContextInputString,
} from '@apps/web/components/admin/admin_agent_page_context';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import { assert } from '@libraries/typescript/utilities';

export type AgentTracePageContextTrace = {
    readonly id?: string | null;
    readonly phoebe_run_id?: string | null;
};

export type AgentTracePageContextResolverInput = {
    readonly page_kind: 'agent_trace';
    readonly trace: AgentTracePageContextTrace;
    readonly environment: unknown;
    readonly pageUrl: string;
};

function isAgentTracePageContextTrace(
    value: unknown,
): value is AgentTracePageContextTrace {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function resolveAgentTracePageContext({
    trace,
    environment,
    pageUrl,
}: {
    readonly trace: AgentTracePageContextTrace;
    readonly environment: unknown;
    readonly pageUrl: string;
}): AdminAgentPageContextResolution {
    const traceId = requireAdminAgentPageContextInputString(
        trace.id,
        'missing_trace_id',
    );
    switch (traceId.status) {
        case 'unavailable':
            return {
                status: 'unavailable',
                reason: traceId.reason,
                context: null,
            };
        case 'available':
            break;
        default:
            return assert.never(traceId);
    }

    const runId = requireAdminAgentPageContextInputString(
        trace.phoebe_run_id,
        'missing_run_id',
    );
    switch (runId.status) {
        case 'unavailable':
            return {
                status: 'unavailable',
                reason: runId.reason,
                context: null,
            };
        case 'available':
            break;
        default:
            return assert.never(runId);
    }

    return {
        status: 'available',
        context: {
            page_kind: 'agent_trace',
            trace_id: traceId.value,
            conversation_id: traceId.value,
            run_id: runId.value,
            environment: normalizeAdminAgentPageContextEnvironment(environment),
            page_url: pageUrl,
        },
    };
}

export function resolveCurrentAgentTracePageContext(
    trace: AgentTracePageContextTrace,
): AdminAgentPageContextResolution {
    return resolveAgentTracePageContext({
        trace,
        environment: getRunEnvironment(),
        pageUrl: window.location.href,
    });
}

export const agentTracePageContextResolver: AdminAgentPageContextResolver = {
    page_kind: 'agent_trace',
    resolve: (input) => {
        switch (input.page_kind) {
            case 'agent_trace': {
                const trace = input['trace'];
                const pageUrl = input['pageUrl'];
                if (
                    !isAgentTracePageContextTrace(trace) ||
                    typeof pageUrl !== 'string'
                ) {
                    return {
                        status: 'unavailable',
                        reason: 'missing_trace_id',
                        context: null,
                    };
                }

                return resolveAgentTracePageContext({
                    trace,
                    environment: input['environment'],
                    pageUrl,
                });
            }
            default:
                return {
                    status: 'unavailable',
                    reason: 'unsupported_page',
                    context: null,
                };
        }
    },
};
